import { useTranslation } from "react-i18next";
import type {
  MessageDirectionName,
  MessageOriginName,
  MessageStatusName,
  MessageTypeName,
  PlatformTypeName,
  ReportMessageOptionsResponse,
} from "@multi-chat/shared";
import { MESSAGE_STATUSES } from "@multi-chat/shared";
import { SimpleSelect } from "../../components/ui/select/index.js";
import { filterActiveReportOrigins, REPORT_ACTIVE_ORIGINS } from "./active-report-origins.js";
import { messageTypeLabelKey } from "./incoming-report-presentation.js";
import {
  originLabelKey,
  statusLabelKey,
} from "./outgoing-report-presentation.js";
import { ReportChannelSelect } from "./ReportChannelSelect.js";

const ALL = "__ALL__";

export type MessageLogFilterValues = {
  direction?: MessageDirectionName;
  platform?: PlatformTypeName;
  endpointId?: number;
  messageType?: MessageTypeName;
  status?: MessageStatusName;
  origin?: MessageOriginName;
  campaignId?: number;
  template?: string;
};

export function countMessageLogActiveFilters(
  values: MessageLogFilterValues,
  showOutboundOnlyFilters: boolean,
): number {
  let count = 0;
  if (values.direction) count += 1;
  if (values.platform) count += 1;
  if (values.endpointId != null) count += 1;
  if (values.messageType) count += 1;
  if (values.status) count += 1;
  if (showOutboundOnlyFilters) {
    if (values.origin) count += 1;
    if (values.campaignId != null) count += 1;
    if (values.template) count += 1;
  }
  return count;
}

export function MessageLogFilters({
  options,
  values,
  onChange,
  idPrefix = "reports-message-log",
  showOutboundOnlyFilters,
}: {
  options: ReportMessageOptionsResponse | null;
  values: MessageLogFilterValues;
  onChange: (patch: Partial<MessageLogFilterValues>) => void;
  idPrefix?: string;
  /** When false (Direction=INCOMING), hide Origin / Campaign / Template. */
  showOutboundOnlyFilters: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const platforms = options?.platforms ?? [];
  const endpoints = options?.endpoints ?? [];
  const messageTypes = options?.messageTypes ?? [];
  const campaigns = options?.campaigns ?? [];
  const templates = options?.templates ?? [];
  const origins = filterActiveReportOrigins(options?.origins ?? [...REPORT_ACTIVE_ORIGINS]);
  const statuses = options?.statuses ?? [...MESSAGE_STATUSES];

  return (
    <div className="grid w-full min-w-0 gap-1.5" data-testid={`${idPrefix}-filters`}>
      <label className="grid w-full min-w-0 gap-0.5 text-[0.75rem]">
        <span className="text-muted">{t("reports.messageLog.filters.direction")}</span>
        <SimpleSelect
          value={values.direction ?? ALL}
          onValueChange={(value) =>
            onChange({
              direction: value === ALL ? undefined : (value as MessageDirectionName),
            })
          }
          ariaLabel={t("reports.messageLog.filters.direction")}
          triggerClassName="h-8 w-full min-w-0 rounded-md border-line/70 text-start text-[0.75rem]"
          options={[
            { value: ALL, label: t("reports.messageLog.filters.allDirections") },
            { value: "INBOUND", label: t("reports.messageLog.directions.INBOUND") },
            { value: "OUTBOUND", label: t("reports.messageLog.directions.OUTBOUND") },
          ]}
        />
      </label>

      <ReportChannelSelect
        value={values.platform}
        platforms={platforms}
        onChange={(platform) => onChange({ platform })}
        allLabel={t("reports.messageLog.filters.allChannels")}
        channelLabel={t("reports.messageLog.filters.channel")}
        labelForPlatform={(platform) => t(`reports.messageLog.platforms.${platform}`)}
        ariaLabel={t("reports.messageLog.filters.channel")}
        testId={`${idPrefix}-channel-filter`}
      />

      <label className="grid w-full min-w-0 gap-0.5 text-[0.75rem]">
        <span className="text-muted">{t("reports.messageLog.filters.account")}</span>
        <SimpleSelect
          value={values.endpointId != null ? String(values.endpointId) : ALL}
          onValueChange={(value) =>
            onChange({
              endpointId: value === ALL ? undefined : Number(value),
            })
          }
          ariaLabel={t("reports.messageLog.filters.account")}
          triggerClassName="h-8 w-full min-w-0 rounded-md border-line/70 text-start text-[0.75rem]"
          options={[
            { value: ALL, label: t("reports.messageLog.filters.allAccounts") },
            ...endpoints.map((endpoint) => ({
              value: String(endpoint.endpointId),
              label: endpoint.secondaryLabel
                ? `${endpoint.displayLabel} · ${endpoint.secondaryLabel}`
                : endpoint.displayLabel,
            })),
          ]}
        />
      </label>

      <label className="grid w-full min-w-0 gap-0.5 text-[0.75rem]">
        <span className="text-muted">{t("reports.messageLog.filters.messageType")}</span>
        <SimpleSelect
          value={values.messageType ?? ALL}
          onValueChange={(value) =>
            onChange({
              messageType: value === ALL ? undefined : (value as MessageTypeName),
            })
          }
          ariaLabel={t("reports.messageLog.filters.messageType")}
          triggerClassName="h-8 w-full min-w-0 rounded-md border-line/70 text-start text-[0.75rem]"
          options={[
            { value: ALL, label: t("reports.messageLog.filters.allMessageTypes") },
            ...messageTypes.map((messageType) => ({
              value: messageType,
              label: t(messageTypeLabelKey(messageType)),
            })),
          ]}
        />
      </label>

      <label className="grid w-full min-w-0 gap-0.5 text-[0.75rem]">
        <span className="text-muted">{t("reports.messageLog.filters.status")}</span>
        <SimpleSelect
          value={values.status ?? ALL}
          onValueChange={(value) =>
            onChange({
              status: value === ALL ? undefined : (value as MessageStatusName),
            })
          }
          ariaLabel={t("reports.messageLog.filters.status")}
          triggerClassName="h-8 w-full min-w-0 rounded-md border-line/70 text-start text-[0.75rem]"
          options={[
            { value: ALL, label: t("reports.messageLog.filters.allStatuses") },
            ...statuses.map((status) => ({
              value: status,
              label: t(statusLabelKey(status)),
            })),
          ]}
        />
      </label>

      {showOutboundOnlyFilters ? (
        <>
          <label className="grid w-full min-w-0 gap-0.5 text-[0.75rem]">
            <span className="text-muted">{t("reports.messageLog.filters.origin")}</span>
            <SimpleSelect
              value={values.origin ?? ALL}
              onValueChange={(value) =>
                onChange({
                  origin: value === ALL ? undefined : (value as MessageOriginName),
                })
              }
              ariaLabel={t("reports.messageLog.filters.origin")}
              triggerClassName="h-8 w-full min-w-0 rounded-md border-line/70 text-start text-[0.75rem]"
              options={[
                { value: ALL, label: t("reports.messageLog.filters.allOrigins") },
                ...origins.map((origin) => ({
                  value: origin,
                  label: t(originLabelKey(origin)),
                })),
              ]}
            />
          </label>

          <label className="grid w-full min-w-0 gap-0.5 text-[0.75rem]">
            <span className="text-muted">{t("reports.messageLog.filters.campaign")}</span>
            <SimpleSelect
              value={values.campaignId != null ? String(values.campaignId) : ALL}
              onValueChange={(value) =>
                onChange({
                  campaignId: value === ALL ? undefined : Number(value),
                })
              }
              ariaLabel={t("reports.messageLog.filters.campaign")}
              triggerClassName="h-8 w-full min-w-0 rounded-md border-line/70 text-start text-[0.75rem]"
              options={[
                { value: ALL, label: t("reports.messageLog.filters.allCampaigns") },
                ...campaigns.map((campaign) => ({
                  value: String(campaign.campaignId),
                  label: campaign.name,
                })),
              ]}
            />
          </label>

          <label className="grid w-full min-w-0 gap-0.5 text-[0.75rem]">
            <span className="text-muted">{t("reports.messageLog.filters.template")}</span>
            <SimpleSelect
              value={values.template ?? ALL}
              onValueChange={(value) =>
                onChange({ template: value === ALL ? undefined : value })
              }
              ariaLabel={t("reports.messageLog.filters.template")}
              triggerClassName="h-8 w-full min-w-0 rounded-md border-line/70 text-start text-[0.75rem]"
              options={[
                { value: ALL, label: t("reports.messageLog.filters.allTemplates") },
                ...templates.map((template) => ({
                  value: template.name,
                  label: template.language
                    ? `${template.name} (${template.language})`
                    : template.name,
                })),
              ]}
            />
          </label>
        </>
      ) : null}
    </div>
  );
}
